"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

const navLinks = [
  { label: 'Intelligence',   href: '/intelligence' },
  { label: 'Research',       href: '/research' },
  { label: 'Solutions', href: '/culture'  },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300`}
        style={{
          background: scrolled ? 'rgba(10,37,64,0.80)' : 'rgba(10,37,64,0.80)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(15,76,129,0.5)',
          fontFamily: 'var(--font-body)',
        }}
      >
        <div
          className="flex items-center justify-between px-6 md:px-12 py-4"
          style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}
        >
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 z-10 group">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #4A90E2 0%, #A3D4FF 100%)' }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M3 14L7 6L11 10L14 4" stroke="#0A2540" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="14" cy="4" r="1.5" fill="#0A2540"/>
              </svg>
            </div>
            <div className="hidden sm:block">
              <span
                className="font-display text-lg font-bold tracking-tight text-white block leading-none"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                DividendChase
              </span>
              <span
                className="text-xs font-mono tracking-widest uppercase"
                style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.2em' }}
              >
                Institutional Research
              </span>
            </div>
          </Link>

          {/* Desktop Nav — center links: Intelligence, Research, Solutions */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks?.map((l) => {
              const active = pathname === l?.href || pathname?.startsWith(l?.href + '/');
              return (
                <Link
                  key={l?.href}
                  href={l?.href}
                  className={`text-sm font-medium transition-colors duration-200`}
                  style={{ color: active ? '#A3D4FF' : '#E3F2FD', opacity: active ? 1 : 0.7 }}
                >
                  {l?.label}
                </Link>
              );
            })}
          </nav>

          {/* Right side: Log In + Access Terminal */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              href="/dashboard"
              className="text-sm font-semibold transition-colors duration-200"
              style={{ color: '#E3F2FD', opacity: 0.7, fontFamily: 'var(--font-body)' }}
            >
              Log In
            </Link>
            <Link
              href="/homepage#pricing"
              className="text-sm font-bold px-5 py-2.5 rounded-full transition-all duration-200"
              style={{
                background: '#A3D4FF',
                color: '#0A2540',
                fontFamily: 'var(--font-body)',
                letterSpacing: '0.03em',
                border: '2px solid #A3D4FF',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                (e.currentTarget as HTMLAnchorElement).style.color = '#A3D4FF';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#A3D4FF';
                (e.currentTarget as HTMLAnchorElement).style.color = '#0A2540';
              }}
            >
              Access Terminal
            </Link>
          </div>

          {/* Hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2 z-10"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} style={{ background: '#A3D4FF' }} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} style={{ background: '#A3D4FF' }} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} style={{ background: '#A3D4FF' }} />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 flex flex-col justify-center items-center transition-all duration-500 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: '#0A2540' }}
      >
        <nav className="flex flex-col items-center gap-8">
          {[...navLinks, { label: 'Dashboard', href: '/dashboard' }]?.map((l, i) => {
            const active = pathname === l?.href;
            return (
              <Link
                key={l?.href}
                href={l?.href}
                onClick={() => setMenuOpen(false)}
                className="text-4xl font-display font-bold transition-colors"
                style={{
                  fontFamily: 'var(--font-display)',
                  color: active ? '#A3D4FF' : '#FFFFFF',
                  transitionDelay: `${i * 60}ms`,
                  transform: menuOpen ? 'translateY(0)' : 'translateY(20px)',
                  opacity: menuOpen ? 1 : 0,
                  transition: `opacity 0.4s ease ${i * 60}ms, transform 0.4s ease ${i * 60}ms, color 0.2s`,
                }}
              >
                {l?.label}
              </Link>
            );
          })}
          <Link
            href="/homepage#pricing"
            onClick={() => setMenuOpen(false)}
            className="btn-primary mt-4"
          >
            Access Terminal
          </Link>
        </nav>
      </div>
    </>
  );
}